import {createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useState} from 'react'
import {useAuth, UserAttachment} from '../../modules/auth'
import {WithChildren} from '../../../_metronic/helpers'
import {getUserAllAttachments} from '../../modules/auth/core/_requests'
import {IMenusAndSubmenus, IRoleDetailsAPIRes, ISubMenu} from '../../modules/Users/user-role/_model'
import {BASE_URL} from '../../modules/config'
import axios from 'axios'
import {useQuery} from 'react-query'
import {IBalance} from '../../pages/dashboard/core/_model'
import {dashboardRequest} from '../../pages/dashboard/dashboardRequest'
import {getMimeTypeFromBase64} from '../helperFunction/files.helper'

type UserStateContextType = {
  userAttachments: UserAttachment
  setUserAttachments: Dispatch<SetStateAction<UserAttachment>>
  roleDetailMenus: IMenusAndSubmenus[]
  sidebarFooterMenus: IMenusAndSubmenus[]
  loadingMenu: boolean
  balance: IBalance | undefined
}

export const GET_ROLE_DETAILS_URL = `${BASE_URL}/api/protect/roleDetails?roleId=`

const initUserStateContextPropsState = {
  userAttachments: {
    usernid: '',
    userprofile: '',
    usertradelicense: '',
  },
  setUserAttachments: () => {},
  roleDetailMenus: [],
  sidebarFooterMenus: [],
  loadingMenu: true,
  balance: undefined,
}

const UserStateContext = createContext<UserStateContextType>(initUserStateContextPropsState)

export function transformRoleDetailsArray(apiRes: IRoleDetailsAPIRes[]): IMenusAndSubmenus[] {
  return apiRes.map((item) => {
    const transformedSubmenus: ISubMenu[] | undefined =
      item.submenus && item.submenus.length > 0
        ? item.submenus.map((sub) => ({
            ...sub,
            submenu_link: sub.link_value,
            view_level: sub.view_level,
          }))
        : undefined

    return {
      ...item.menus,
      menu_link: item.menus.link_value,
      view_level: item.menus.view_level,
      ...(transformedSubmenus ? {submenus: transformedSubmenus} : {}),
    }
  })
}

const UserStateProvider: FC<WithChildren> = ({children}) => {
  const {currentUser, auth} = useAuth()
  const [userAttachments, setUserAttachments] = useState<UserAttachment>(
    initUserStateContextPropsState.userAttachments
  )
  const [loadingMenu, setLoadingMenu] = useState<boolean>(
    initUserStateContextPropsState.loadingMenu
  )
  const [roleDetailMenus, setRoleDetailMenus] = useState<IMenusAndSubmenus[]>(
    initUserStateContextPropsState.roleDetailMenus
  )
  const [sidebarFooterMenus, setSidebarFooterMenus] = useState<IMenusAndSubmenus[]>(
    initUserStateContextPropsState.roleDetailMenus
  )

  const {data: balance} = useQuery<IBalance | undefined>({
    queryKey: 'balance',
    queryFn: async () => {
      const data = await dashboardRequest<IBalance>('/api/protect/balance', auth?.accessToken)
      return data
    },
    enabled: !!currentUser,
    refetchInterval: process.env.NODE_ENV === 'development' ? 5000000 : 5000,
  })

  const getAttachments = async () => {
    try {
      const attachments = await getUserAllAttachments()
      const mimeType = getMimeTypeFromBase64(attachments.userprofile)
      const photosrc = `data:${mimeType};base64,${attachments.userprofile}`
      setUserAttachments((prevState) => ({...prevState, userprofile: photosrc}))
    } catch (e) {
      console.warn('Failed to load photo:', e)
    }
  }

  const getUserRoleDetails = async (id: number) => {
    try {
      setLoadingMenu(true)
      const response = await axios.get<IRoleDetailsAPIRes[]>(`${GET_ROLE_DETAILS_URL}${id}`)
      const transformed = transformRoleDetailsArray(response.data)
      const footerMenus = transformed.filter(
        (menu) =>
          menu.menu_name.toLowerCase() === 'notification' ||
          menu.menu_name.toLowerCase() === 'profile settings' ||
          menu.menu_name.toLowerCase() === 'users and teams'
      )
      const sidebarmenus = transformed.filter(
        (menu) => !footerMenus.some((f) => f.id_menu === menu.id_menu)
      )
      setSidebarFooterMenus(footerMenus || [])
      setRoleDetailMenus(sidebarmenus || [])
      setLoadingMenu(false)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (currentUser) {
      getAttachments()
      getUserRoleDetails(currentUser.author_id)
    }
  }, [currentUser])

  return (
    <UserStateContext.Provider
      value={{
        userAttachments,
        setUserAttachments,
        loadingMenu,
        roleDetailMenus,
        sidebarFooterMenus,
        balance,
      }}
    >
      {children}
    </UserStateContext.Provider>
  )
}

const useUserStateProvider = () => {
  const context = useContext(UserStateContext)

  if (!context) {
    throw new Error('User State provider should inside')
  }

  return context
}

export {UserStateProvider, useUserStateProvider}
